import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import useTemplates from "../../../services/Whatsapp/useTemplates";
import NoIndex from "../../../components/NoIndex";
import "../../whatsapp/WhatsApp.css";
import { ToastContainer } from "react-toastify";

const TemplateView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getTemplateDetails, loading } = useTemplates();

  const [refreshing, setRefreshing] = useState(false);
  const [template, setTemplate] = useState({});
  const [templateId, setTemplateId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const name = params.get("templateName");

    if (name && name !== templateId) {
      setTemplateId(name);
    }
  }, [location.search]);

  const fetchDetails = async () => {
    if (!templateId) return;

    setRefreshing(true);

    try {
      const data = await getTemplateDetails(templateId);

      if (data) {
        setTemplate(data);
        setError("");
      } else {
        setTemplate({});
        setError("Failed to load template");
      }
    } catch (err) {
      setError("Something went wrong");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [templateId]);

  const components = useMemo(() => {
    return Array.isArray(template?.components) ? template.components : [];
  }, [template]);

  const headerComp = components.find((c) => c?.type === "HEADER") || {};
  const bodyComp = components.find((c) => c?.type === "BODY") || {};
  const footerComp = components.find((c) => c?.type === "FOOTER") || {};
  const btnComp = components.find((c) => c?.type === "BUTTONS") || {
    buttons: [],
  };

  return (
    <>
      <NoIndex />
<ToastContainer/>
      <div className="h-full flex flex-col overflow-hidden montserrat-font-1">
        {/* Top Header */}
        <div className="sticky top-0 z-30 rounded-xl mx-5 mt-3  bg-white px-6 py-4 flex justify-between items-center border-b flex-shrink-0 montserrat-font-1">
          <div className="montserrat-font-1">
            <h2 className="text-xl font-semibold">
              Template Details
              {template?.name && (
                <span className="text-gray-500 text-sm ml-2">
                  ({template.name})
                </span>
              )}
            </h2>
          </div>

          <div className="flex items-center gap-3 montserrat-font-1">
            {/* Status Badge */}
            {template?.status && (
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                {template.status}
              </span>
            )}

            {/* Category Badge */}
            {template?.category && (
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-600">
                {template.category}
              </span>
            )}

            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-3 py-2 border rounded-lg text-gray-700 hover:bg-gray-100"
            >
              <ArrowLeft size={16} />
              Back
            </button>

            <button
              onClick={fetchDetails}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* Main Content */}
     <div className="flex-1 overflow-y-auto min-h-0 p-6">
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 montserrat-font-1">
          {/* LEFT SIDE DETAILS */}
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            {refreshing ? (
              <>
                {Array.from({ length: 10 }).map((_, i) => (
                  <DetailSkeleton key={i} />
                ))}
              </>
            ) : (
              <>
                <Detail label="Template ID" value={template?.id} />
                <Detail label="Template Name" value={template?.name} />
                <Detail label="Language" value={template?.language} />
                <Detail label="Status" value={template?.status} badge />
                <Detail label="Category" value={template?.category} />
                <Detail label="Header Type" value={headerComp?.format} />
                <Detail label="Header Text" value={headerComp?.text} />
                <Detail label="Body Type" value={bodyComp?.format} />
                <Detail label="Body Text" value={bodyComp?.text} />
                <Detail label="Footer Text" value={footerComp?.text} />

                <Detail
                  label="Buttons"
                  value={
                    btnComp?.buttons?.length
                      ? btnComp.buttons.map((b) => b.text).join(", ")
                      : "No Buttons"
                  }
                />
              </>
            )}
          </div>

          {/* RIGHT SIDE MOBILE PREVIEW (SAME CLASSNAMES PRESERVED) */}
          <div className="bg-white rounded-xl border shadow-sm p-6 flex justify-center montserrat-font-1 items-center">
            <div className="flex justify-center items-start mt-4 montserrat-font-1">
              <div
                className="mobile-card montserrat-font-1"
                style={{ position: "relative", marginTop: "1rem" }}
              >
                {/* Side Buttons */}
                <div className="mobile-btn1"></div>
                <div className="mobile-btn2"></div>
                <div className="mobile-btn3"></div>
                <div className="mobile-btn4"></div>

                {/* SCREEN */}
                <div className="mobile-card-int">
                  <div className="bubble-container">
                    <div className="message-bubble message-bubble-animate">
                      {refreshing ? (
                        <div className="space-y-2">
                          <div className="shimmer h-20 w-full"></div>
                          <div className="shimmer h-4 w-3/4"></div>
                          <div className="shimmer h-4 w-1/2"></div>
                        </div>
                      ) : (
                        <>
                          {/* HEADER TEXT */}
                          {headerComp?.format === "TEXT" &&
                            headerComp?.text && (
                              <div className="header-text">
                                {headerComp.text}
                              </div>
                            )}

                          {/* HEADER IMAGE */}
                          {headerComp?.format === "IMAGE" && (
                            <img
                              src={
                                headerComp?.image_url ||
                                headerComp?.example?.header_handle?.[0]
                              }
                              alt="header"
                              style={{ width: "100%", borderRadius: "5px" }}
                            />
                          )}

                          {/* HEADER VIDEO */}
                          {headerComp?.format === "VIDEO" && (
                            <video
                              controls
                              src={
                                headerComp?.video_url ||
                                headerComp?.example?.header_handle?.[0]
                              }
                              style={{ width: "100%", borderRadius: "5px" }}
                            />
                          )}

                          {/* HEADER DOCUMENT */}
                          {headerComp?.format === "DOCUMENT" && (
                            <div className="header-text">
                              📄 {headerComp?.example?.header_handle?.[0]}
                            </div>
                          )}

                          {bodyComp?.text && (
                            <div className="body-text py-1">
                              {bodyComp.text}
                            </div>
                          )}

                          {footerComp?.text && (
                            <div className="footer-text">{footerComp.text}</div>
                          )}

                          <div className="footer-text text-end">
                            {new Date().toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* NOTCH — IMPORTANT: MUST BE LAST */}
                <div className="mobile-top">
                  <div className="mobile-camera">
                    <div className="mobile-int"></div>
                  </div>
                  <div className="mobile-speaker"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        {loading && (
          <div className="text-center py-6 text-gray-500">Loading...</div>
        )}

        {error && <div className="text-center py-6 text-red-500">{error}</div>}
      </div>
    </>
  );
};

const DetailSkeleton = () => (
  <div className="grid grid-cols-2 px-6 py-4 border-b border-dashed montserrat-font-1">
    <div className="shimmer h-4 w-24"></div>
    <div className="shimmer h-4 w-32"></div>
  </div>
);

const Detail = ({ label, value, badge }) => (
  <div className="grid grid-cols-2 px-6 py-4 border-b border-dashed">
    <div className="text-sm font-medium text-gray-500">{label}</div>
    <div className="text-sm text-gray-800 break-words">
      {badge ? (
        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
          {value || "—"}
        </span>
      ) : (
        value || "—"
      )}
    </div>
  </div>
);

export default TemplateView;
